import { useEffect, useState } from 'react';
import { isNil } from 'lodash';
import { interpolateString } from '@utils/string';
import { FEATURE_FLAG_PREFIX, FeatureFlag } from '@constants/featureFlags';

const getFeatureFlag = (flagName: string): boolean => {
  if (isNil(window)) return false;

  try {
    const storageKey = interpolateString(FEATURE_FLAG_PREFIX, { featureFlag: flagName }, false);
    const item = localStorage.getItem(storageKey as string);
    return item === 'true';
  } catch {
    return false;
  }
};

/**
 * A hook for handling feature flags.
 * This hook checks `localStorage` to determine if a feature is enabled.
 * To enable a feature flag, set the corresponding item in `localStorage`.
 *
 * @param flagName The name of the feature flag.
 * @returns A boolean indicating whether the feature is enabled.
 *
 * @example
 * In the browser console:
 * ```js
 * localStorage.setItem('feature-flag-new-feature', 'true');
 * ```
 *
 * In a React component:
 * ```tsx
 * import { useFeatureFlag } from '@hooks/useFeatureFlag';
 *
 * const MyComponent = () => {
 *   const isNewFeatureEnabled = useFeatureFlag('new-feature');
 *
 *   if (!isNewFeatureEnabled) return null;
 *
 *   return <div>The new feature is here!</div>;
 * };
 * ```
 */
export const useStorageFeatureFlag = (flagName: FeatureFlag): boolean => {
  const [isEnabled, setIsEnabled] = useState(() => getFeatureFlag(flagName));

  useEffect(() => {
    setIsEnabled(getFeatureFlag(flagName));
  }, [flagName]);

  return isEnabled;
};
